import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { AdminPublic } from "@/shared/api";
import {
  isAuthenticated,
  logout as dropToken,
  me,
  setSessionExpiredHandler,
} from "@/shared/api";

import type { AuthState } from "./context";
import { AuthContext } from "./context";

/**
 * Сессия администратора.
 *
 * Живёт в `entities`, а не в `app`: её читают и страницы (гейтинг роутов по
 * роли), и виджеты (шапка с логином). Провайдер в слое app означал бы импорт
 * «вверх» из каждого такого места — ровно то, что запрещает FSD и ловит
 * линтер.
 *
 * Токен переживает перезагрузку страницы, а объект администратора — нет,
 * поэтому при старте он перечитывается с бэкенда. Заодно это проверка, что
 * токен ещё жив и учётку не отключили: 401 здесь означает форму входа, а не
 * админку с неработающими кнопками.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminPublic | null>(null);
  const [loading, setLoading] = useState(isAuthenticated);
  const queryClient = useQueryClient();

  const logout = useCallback(() => {
    dropToken();
    setAdmin(null);
    // Кэш чужой сессии оставлять нельзя: следующий вошедший увидел бы данные
    // предыдущего до первого рефетча.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    // Клиент сам стирает токен на 401; провайдеру остаётся убрать состояние и
    // кэш. Регистрация здесь, а не в модуле api, чтобы shared не знал о React.
    setSessionExpiredHandler(logout);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated()) return;
    let cancelled = false;
    void me()
      .then((current) => {
        if (!cancelled) setAdmin(current);
      })
      .catch(() => {
        // 401 уже обработан клиентом: токен стёрт, состояние сброшено.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      admin,
      loading,
      isSuperadmin: admin?.role === "superadmin",
      setAdmin,
      logout,
    }),
    [admin, loading, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
